import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import LayoutManager from '@managers/LayoutManager';
import * as mixpanel from 'mixpanel-browser';

import { fetchAgencyPublicInfo } from '@actions/agency';
import { addError, clearAllAlerts } from '@actions/appState';
import { loginSuccess, superAdminAgencyLogin } from '@actions/auth';
import { fetchProductLayout } from '@actions/productLayout';

import { getLandingPagePartialURL } from '@utils/AppUtil';
import { authService, getUserProfile, isLoggedIn, saveCurrentAgency } from '@utils/AuthUtil';
import { getFormattedDeviceDetails } from '@utils/DeviceDetailsUtil';
import { track } from '@utils/MixPanelUtils';
import { consumePostLoginRedirectUrl } from '@utils/PostLoginRedirectUtil';

import './style.scss';

interface ILoginProps extends RouteComponentProps<{ agencyId: string }> {
  // from redux
  userProfile: IUserProfile;
  loginSuccess: TLoginSuccess;
  superAdminAgencyLogin: TSuperAdminAgencyLogin;
  clearAllAlerts: TClearAllAlerts;
  fetchAgencyPublicInfo: TFetchAgencyPublicInfo;
  addError?: TAddError;
  fetchProductLayout?: TFetchProductLayout;
}

interface ILoginState {
  user: {
    email: string;
    password: string;
  };
  error: {
    email: IFieldValidation;
    password: IFieldValidation;
  };
  isOktaAgency: boolean;
  isFetchingConfig: boolean;
  agencyId: number;
  SSOProvider: SSOProvider;
}

class Login extends React.Component<ILoginProps, ILoginState> {
  constructor(props: ILoginProps) {
    super(props);

    this.state = this.getEmptyState();
  }

  async componentDidMount() {
    if (this.props.match.params.agencyId) {
      const newState: ILoginState = this.state;
      newState.agencyId = Number(this.props.match.params.agencyId);

      this.setState(newState);
    }

    const superAdminToken = this.getSuperAdminToken();
    if (superAdminToken) {
      this.props.superAdminAgencyLogin(superAdminToken);
    }

    if (isLoggedIn()) {
      const userProfile: IUserProfile = getUserProfile();

      if (userProfile?.agencyId && !LayoutManager.isProductLayoutPresent()) {
        await LayoutManager.refreshLayout([this.props.fetchProductLayout]);
      }
      this.redirectUser(userProfile);
    }

    this.updateAgencyData();
  }

  showOKTAHostedLoginPage = async () => {
    await authService.signInWithRedirect();
  };

  componentDidUpdate(prevProps: Readonly<ILoginProps>, prevState: Readonly<ILoginState>, snapshot?: any): void {
    const currentAgencyId = this.props.match?.params?.agencyId;
    const prevAgencyId = prevProps.match?.params?.agencyId;
    if (this.props.userProfile && this.props.userProfile !== prevProps.userProfile) {
      mixpanel.identify(getUserProfile().email);

      track('USER_LOGGED_IN', {
        'Device Details': getFormattedDeviceDetails(),
      });
      this.redirectUser(this.props.userProfile);
    }
    if (currentAgencyId && currentAgencyId !== prevAgencyId) {
      this.updateAgencyData();
    }
  }

  async updateAgencyData() {
    this.props.fetchAgencyPublicInfo(this.state.agencyId).then((agencyPublicInfo) => {
      if (agencyPublicInfo.response === null) {
        this.props.history.push('error');
      } else {
        this.setState({
          isFetchingConfig: false,
          SSOProvider: agencyPublicInfo.response.SSOProvider,
        });
        if (
          agencyPublicInfo.response.SSOProvider === SSOProvider.OKTA ||
          agencyPublicInfo.response.SSOProvider === SSOProvider.AD
        ) {
          // saving current agency info due to oauth redirect flow
          saveCurrentAgency(agencyPublicInfo.response);
          this.setState({ isOktaAgency: true });
        }
        if (!isLoggedIn()) {
          this.showOKTAHostedLoginPage();
        }
      }
    });
  }

  getSuperAdminToken = () => {
    const search = this.props.location.search;
    const params = new URLSearchParams(search);

    return params.get('token');
  };

  redirectUser = (userProfile: IUserProfile) => {
    this.props.loginSuccess(userProfile);
    const redirectUrl = consumePostLoginRedirectUrl();
    if (redirectUrl) {
      this.props.history.push(redirectUrl);
    } else if (this.state.agencyId) {
      const landingPage = getLandingPagePartialURL(userProfile);
      if (landingPage !== '/') {
        this.props.history.push(`/agencies/${this.state.agencyId}/${landingPage}`);
      } else {
        this.props.addError('User account does not have necessary privileges to log in.');
      }
    } else {
      this.props.history.push('/');
    }
  };

  getErrorEmptyState = (): IFieldValidation => ({
    errorMessage: '',
    errorState: null,
  });

  getEmptyState = (): ILoginState => ({
    isOktaAgency: false,
    user: {
      email: '',
      password: '',
    },
    error: {
      email: this.getErrorEmptyState(),
      password: this.getErrorEmptyState(),
    },
    agencyId: null,
    isFetchingConfig: true,
    SSOProvider: null,
  });

  render() {
    return null;
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  userProfile: state.auth.userProfile,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchProductLayout: () => dispatch(fetchProductLayout()),
  loginSuccess: (userProfile: IUserProfile) => dispatch(loginSuccess(userProfile)),
  addError: (errorMessage: string) => dispatch(addError(errorMessage)),
  superAdminAgencyLogin: (jwtToken: string) => dispatch(superAdminAgencyLogin(jwtToken)),
  clearAllAlerts: () => dispatch(clearAllAlerts()),
  fetchAgencyPublicInfo: (agencyId: number) => dispatch(fetchAgencyPublicInfo(agencyId)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Login));
